import { combineLatest, forkJoin, interval, Observable, of, zip } from 'rxjs';
import { catchError, concatMap, defaultIfEmpty, first, flatMap, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { DataStoreOptions, ItemOfflineDataFetcher, OfflineDataFetcher, SearchResponse } from '@skinsoft/data-store';
import { API_ENDPOINTS, Expression, ExpressionDataStore, Manifestation, ManifestationDataStore, Work, WorkDataStore } from '@skinsoft/entity';
import { EntityDataStore } from '@skinsoft/entity-data-store';
import { searchResultSelectors, wemiSelectors } from '@skinsoft/search-data-store';
import _ from 'lodash';
import { Wemi, WemiUtils } from '../wemi-utils';

export type SearchResult = Item & Manifestation & Expression & Work;

declare var ITEM_SEARCH_TYPES: string[];

export interface Item {
    id: string;
    identificationItemSchema: {
        barCodes: {
            translatedString: {
                fr: string;
            };
        };
        identifiers: any[]
    };
    associatedMedias: any
    stateItemSchema: any
    type: string
    linkToManifestation: {
        referentManifestation: string
    },
    institutionCreation: string

}

export class ItemDataStore extends EntityDataStore<Item, any, SearchResult | Wemi> {
    public static getInstance() {
        if (!ItemDataStore.instance) {
            ItemDataStore.instance = new ItemDataStore();
        }

        return ItemDataStore.instance;
    }

    public static install(Vue) {
        Vue.prototype.$itemDataStore = ItemDataStore.getInstance();
    }

    public static getItemSearchResultId(itemSearchResult: SearchResult | Wemi) {
        if ("id" in itemSearchResult) {
            return searchResultSelectors.id(itemSearchResult);
        } else {
            return wemiSelectors.id(itemSearchResult);
        }
    }

    private static instance: ItemDataStore = null as any;

    constructor() {
        super('items', ITEM_SEARCH_TYPES);
    }

    public exists(id: Item['id']) {
        return this.fetchOne(id)
            .pipe(first())
            .toPromise();
    }

    public searchAll(queryText: string | null, sortObject: any, pageSize: number = 15, types?: string[], page: number = 0, advancedSearches: any = null, filteredIds?: string[], pathAndValueToCheck?: any, searchEndpointOverride: string | null = null, options: DataStoreOptions = {})
        : Observable<SearchResponse<SearchResult | Wemi>> {
        return super.searchAll(queryText, sortObject, pageSize, types, page, advancedSearches, filteredIds, pathAndValueToCheck, searchEndpointOverride, options);
    }
    
    public searchAllOffline(queryText: string | null, sortObject: any, pageSize: number = 15, types?: string[], page: number = 0, advancedSearches: any = null, filteredIds?: string[], pathAndValueToCheck?: any, searchEndpointOverride: string | null = null, options: DataStoreOptions = {})
        : Observable<SearchResponse<SearchResult | Wemi>> {
        return super.searchAllOffline(queryText, sortObject, pageSize, types, page, advancedSearches, filteredIds, pathAndValueToCheck, searchEndpointOverride, options)
                .pipe(
                    flatMap(this.addWemi),
                    map(this.mergeWemi)
                );
    }

    public addWemi(data): Observable<[any, SearchResponse<SearchResult | Wemi>]> {
        const allWemi = data.allData.map(item => {
            return WemiUtils.getWemiFromItem(item.id)
        })

        return combineLatest([forkJoin(allWemi).pipe(defaultIfEmpty(data)), of(data)])
    }

    public mergeWemi(data): SearchResponse<SearchResult | Wemi> {
        return {...data, allData: _.mergeWith(data[1].allData, data[0], (item, wemi) => ({
            ...wemi,
            item,
            work: {
                ...wemi.work,
                indexTitle: wemi.work?.descriptionMuseumWorkSchema?.workTitles[0]?.title
            }
        }))}
    }

    public pushUpdate(dataToUpdate) {
            if (dataToUpdate.id && dataToUpdate.body) {
                this.synchronizationMap[dataToUpdate.id] = 'inProgress'
                return this.getWemiFromItem(
                    dataToUpdate.id,
                )
                    .pipe(
                        mergeMap(async wemi => {
                            const item = await this.create().toPromise()
                            wemi = await WemiUtils.mergeWemiWithNewItem(wemi, item)
                            _.set(wemi, 'item.associatedMedias.first', null)

                            const fixSubelement = ((object: { [key: string]: any | any[] }) => {
                                for (const [key, value] of Object.entries(object)) {
                                    if (typeof value === 'object' && _.isEmpty(value)) {
                                        _.set(object, key, null)
                                    } else if (Array.isArray(value)) {
                                        value.forEach(e => fixSubelement(e))
                                        _.set(object, key, value.filter(o => !(typeof o === 'object' && o !== null && _.isEmpty(o))))
                                    } else if (typeof value === 'object' && value !== null) {
                                        fixSubelement(value)
                                    }
                                }
                            })
                    
                            fixSubelement(wemi)
                            
                            return wemi
                        }),
                        mergeMap(WemiUtils.saveWemi),
                        tap(wemi => this.synchronizationMap[dataToUpdate.id] = wemi.find((wemiElement: { favType: string; }) => wemiElement.favType === 'item').id),
                        tap(async wemi => {
                            const timer = interval(1000)
                            // tslint:disable-next-line: no-shadowed-variable
                            zip(dataToUpdate.mediaSubject, timer).pipe(map(([a, _]) => a)).subscribe((e) => {
                                if ((e as any).newMediaId) {
                                    this.addImage(wemi.find((wemiElement: { favType: string; }) => wemiElement.favType === 'item'), e)
                                }
                            },
                            () => {/**/},
                            async () => {
                                if (!dataToUpdate.body.associatedMedias.first) {
                                    const itemId = wemi.find((wemiElement: { favType: string; }) => wemiElement.favType === 'item').id
                                    const fullItem = await this.fetchOne(itemId).toPromise()

                                    fullItem.associatedMedias.first = null

                                    this.update(itemId, fullItem)
                                }
                            })
                        }),
                        first(),
                        // Handle success or error if the update and emit boolean
                        catchError(e => of(Error('Error during update...'))),
                        concatMap(() => WemiUtils.getWemiFromItemOffline(dataToUpdate.id)),
                        tap(WemiUtils.deleteWemi),
                        switchMap(
                            response => {
                                if (!(response instanceof Error)) {
                                    return of(true)
                                }
                                return of(false);
                            }
                        )
                    )
            }
            else {
                return of(true);
            }
    }

    public update(id: string, body: Partial<Item>, endpointOverride?: string): Observable<Item> {
        return super.update(id, body, endpointOverride || API_ENDPOINTS[body.type as string])
    }

    public getWemiFromItem(itemId: string): Observable<Wemi> {
        if (OfflineDataFetcher.isTemporaryId(itemId)) {
            return WemiUtils.getWemiFromItemOffline(itemId)
        }
        return WemiUtils.getWemiFromItem(itemId)
    }

    public create(createBody?: Partial<Item>, body?: Partial<Item>) {
        const uri = '/physical-museum-items';
        if (window.navigator.onLine || !body) {
            return this.proxy.fetch(uri, createBody, 'POST').pipe(
                concatMap(response => {
                    if (!body) {
                        return of(response);
                    }
                    // In case of a locally created entity, replacing temporary id by the one from db
                    const id: string = _.get(body, "id", '')
                    if (id && OfflineDataFetcher.isTemporaryId(id)) {
                        Object.defineProperty(body, "id", { value: response.id })
                    }
                    return this.update(response.id, _.merge(response, body));
                }),
                switchMap(({ id }) => this.fetchOne(id)),
            );
        } else { // Offline mode
            // the '_' prefix of the id identifies the id as temporary in the cache keys
            return this.createOffline(body);
        }
    }

    public addImage(item: Item, image) {
        this.proxy.fetch(`/physical-museum-items/addMedia/${item.id}`,{idMedia: image.newMediaId}, 'PUT')
    }

    protected initializeDataFetcher() {
        this.offlineDataFetcher = new ItemOfflineDataFetcher(this.sanitizedIdentifer);
    }
}
